import { createPublicClient, http, defineChain } from "viem";

export const VAULT = "0x88CddADA26386d39BDf79B67Fd02d9C0274ED5F9";
export const USDC = "0x2B3370eE501B4a559b57D449569354196457D8Ab";
export const HOUSE_URL = "https://variance-vault-keeper.fly.dev/v14";
export const SITE = "https://testnet.variancevault.xyz";
export const RPC = "https://rpc.hyperliquid-testnet.xyz/evm";
export const CHAIN_ID = 998;

export const hyperEvmTestnet = defineChain({
  id: CHAIN_ID,
  name: "HyperEVM Testnet",
  nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
  rpcUrls: { default: { http: [RPC] } },
  // Canonical Multicall3, verified deployed on HyperEVM testnet. Without this the SDK's
  // registration verify falls back to reading every node/child word in its own eth_call —
  // ~3400 sequential round-trips to skip one 483-node game.
  contracts: { multicall3: { address: "0xcA11bde05977b3631167028862bE2a173976CA11" } },
});

/** The public HyperEVM RPC rate-limits hard (-32005) and a 483-node registration is ~35 txs
 *  worth of reads. Serialize every request behind a minimum interval and back off on 429/-32005
 *  instead of letting viem's parallel retries hammer it harder. */
export function throttledFetch({ minIntervalMs = 120, maxRetries = 5, timeoutMs = 30_000 } = {}) {
  let chain = Promise.resolve();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  return (url, init) => {
    const run = chain.then(async () => {
      let lastErr;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        await sleep(minIntervalMs);
        try {
          // Own deadline per attempt: a hung socket must never wedge the shared queue.
          const res = await fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
          const text = await res.text();
          if ((res.status === 429 || /rate limited|-32005/.test(text)) && attempt < maxRetries) {
            await sleep(700 * 2 ** attempt); continue;
          }
          return new Response(text, { status: res.status, headers: { "content-type": "application/json" } });
        } catch (e) {
          lastErr = e;
          if (attempt >= maxRetries) break;
          await sleep(700 * 2 ** attempt);
        }
      }
      throw lastErr;
    });
    chain = run.then(() => {}, () => {});   // never let a rejection stall the queue
    return run;
  };
}

export const publicClient = createPublicClient({
  chain: hyperEvmTestnet,
  transport: http(RPC, { retryCount: 3, retryDelay: 2000, timeout: 120_000, fetchFn: throttledFetch() }),
});
